import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Redirect } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
} from '@mui/material';
import axios from 'src/utils/axios';
import PerfectScrollbar from 'react-perfect-scrollbar';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Toolbar from 'src/views/management/ChatView/ChatDetails/Toolbar';
import DefaultMessagesView from 'src/views/management/ChatView/ChatDetails/DefaultMessagesView';
import LoadMoreButton from 'src/views/management/ChatView/styled/LoadMoreButton';
import Message from 'src/views/management/ChatView/ChatDetails/Message';
import ScheduledMessagesList from 'src/views/management/ChatView/ChatDetails/ScheduledMessagesList';
import MessageAdd from 'src/views/management/ChatView/NewMessage';
import SmartComposeModal from 'src/components/SmartCompose/SmartComposeModal';
import ReportNotDeliveredModal from 'src/components/ReportNotDelivered/ReportNotDeliveredModal';
import { addNotification } from 'src/actions/notificationActions';
import { clearCustomerData, getCustomer } from 'src/actions/customerDetailsActions';
import {
  clearFilteredChatMessages,
  getMoreChatMessages,
  getMoreScheduledChatMessages,
  markChatAsSeen,
  setFileUploadToMessage,
} from 'src/actions/chatActions';
import {
  MESSAGES,
  NOTIFICATION_STATUS,
  PERMISSIONS,
  ROUTES,
} from 'src/constants';
import helpers from 'src/helpers';

const CARRIER_KEYWORD_MAP = [
  { id: 'tmobile', keywords: ['t-mobile', 'omnipoint', 'metropcs', 'sprint', 'voicestream'] },
  { id: 'att', keywords: ['at&t', 'cingular', 'cricket'] },
  { id: 'verizon', keywords: ['verizon', 'cellco', 'tracfone', 'visible'] },
];

function matchCarrierName(carrierName) {
  if (!carrierName) return null;
  const lower = carrierName.toLowerCase();
  const match = CARRIER_KEYWORD_MAP.find(
    ({ keywords }) => keywords.some((kw) => lower.includes(kw)),
  );
  return match?.id || null;
}

function chatSelector(state, chatKey) {
  const { chats, filteredChats, messagesFilteredByPhone } = state.chat;
  const chatSource = messagesFilteredByPhone && filteredChats ? filteredChats : chats;
  const chat = chatSource.byKey[chatKey];

  // This allows to work with chats before they are created.
  // If it is new, we don't have it in the store. instead, we return an `empty` chat
  if (!chat) {
    return {
      key: chatKey,
      messages: [],
      scheduledMessages: [],
      unreadCount: 0,
      nextCursor: null,
      scheduledNextCursor: null,
    };
  }

  return chat;
}

function ChatDetails() {
  const dispatch = useDispatch();
  const { chatKey } = useParams();
  const [showScrollToBottomButton, setShowScrollToBottomButton] = useState(false);
  const [showScheduledMessages, setShowScheduledMessages] = useState(false);
  const messagesRef = useRef(null);
  const scrollPositionRef = useRef(null);
  const hasAttemptedLoadRef = useRef(new Set());

  const chat = useSelector((state) => chatSelector(state, chatKey));
  const {
    isMessagesLoading,
    messagesFilteredByPhone,
    userCanDeleteTexts,
    deletedChatKey,
  } = useSelector((state) => state?.chat || {});
  const { customer: contact } = useSelector((state) => state?.customerDetails || {});
  const {
    user,
    rateLimits,
  } = useSelector((state) => state?.account || {});

  const {
    permissions: userRolePermissions,
  } = user?.userRole || {};

  const messages = chat.messages || [];
  const scheduledMessages = chat.scheduledMessages || [];
  const contactPhones = helpers.transform
    .toArray([contact?.phone1, contact?.phone2, contact?.phone3])
    .filter((phone) => !helpers.isEmpty(phone));

  const canLoadMoreMessages = !helpers.isEmpty(messages)
    && messages.length >= MESSAGES.CHAT_MESSAGES_LIMIT_PER_REQUEST
    && !helpers.isEmpty(chat.nextCursor);

  const canLoadMoreScheduledMessages = !helpers.isEmpty(scheduledMessages)
    && scheduledMessages.length >= MESSAGES.CHAT_MESSAGES_LIMIT_PER_REQUEST
    && !helpers.isEmpty(chat.scheduledNextCursor);

  const isMoreThanOnePhoneNumber = contactPhones.length > 1;

  const sendingLimits = useMemo(() => helpers.transform.toSMSSendingLimits(rateLimits) || {}, [rateLimits]);

  const isCanadian = user?.country === 'Canada';
  const canViewTestDeliverability = helpers.permissions.doesUserHavePermission(
    PERMISSIONS.PERMISSIONS_SYSTEM_CODE.TEST_DELIVERABILITY_VIEW,
    userRolePermissions,
  ) && !isCanadian;

  const canReportNotDelivered = helpers.permissions.doesUserHavePermission(
    PERMISSIONS.PERMISSIONS_SYSTEM_CODE.REPORT_NOT_DELIVERED_VIEW,
    userRolePermissions,
  );

  // SmartComposeModal state for Test Deliverability from chat messages
  const [smartComposeOpen, setSmartComposeOpen] = useState(false);
  const [smartComposeMessage, setSmartComposeMessage] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [scoreData, setScoreData] = useState(null);
  const [scoreError, setScoreError] = useState(null);

  const handleTestDeliverability = useCallback((message) => {
    setSmartComposeMessage(message);
    setSmartComposeOpen(true);
  }, []);

  const handleSmartComposeClose = useCallback(() => {
    setSmartComposeOpen(false);
    setSmartComposeMessage(null);
    setScoreData(null);
    setScoreError(null);
  }, []);

  // Fetch message score when SmartCompose modal opens
  useEffect(() => {
    if (!smartComposeOpen || !smartComposeMessage?.body) return;

    setScoreLoading(true);
    setScoreError(null);
    axios.post('/api/message-score/analyze', {
      messageText: smartComposeMessage.body,
    })
      .then((res) => {
        setScoreData(res.data);
      })
      .catch((err) => {
        setScoreError(err?.response?.data?.error || 'Unable to score message');
      })
      .finally(() => {
        setScoreLoading(false);
      });
  }, [smartComposeOpen, smartComposeMessage]);

  // Determine carrier for the message's phone number
  const smartComposeCarrier = useMemo(() => {
    if (!smartComposeMessage?.phone || !contact) return { defaultCarrier: null, unsupportedCarrierName: null };

    const normalizedPhone = (smartComposeMessage.phone || '').replace(/\D/g, '').slice(-10);
    const phoneInfoList = [
      { phone: contact.phone1, info: contact.phone1Info },
      { phone: contact.phone2, info: contact.phone2Info },
      { phone: contact.phone3, info: contact.phone3Info },
    ];

    const match = phoneInfoList.find(({ phone }) => {
      const norm = (phone || '').replace(/\D/g, '').slice(-10);
      return norm && norm === normalizedPhone;
    });

    const carrierName = match?.info?.carrier;
    if (!carrierName) return { defaultCarrier: null, unsupportedCarrierName: null };

    const testId = matchCarrierName(carrierName);
    if (testId) return { defaultCarrier: testId, unsupportedCarrierName: null };

    return { defaultCarrier: null, unsupportedCarrierName: carrierName };
  }, [smartComposeMessage, contact]);

  // Report Not Delivered modal state
  // CON-2052. Look it up on one inbound message. The press is handed to the
  // compose box's AiLookup, which owns the guards and the re-run confirm, so
  // the chip and the menu item cannot drift apart. lookItUpState comes back
  // from AiLookup: hidden (not the support account, or no current draft),
  // ready, or pending (a run is in flight, so the item is disabled).
  const [aiLookupRequest, setAiLookupRequest] = useState(null);
  const [lookItUpState, setLookItUpState] = useState('hidden');
  const handleLookItUp = useCallback((message) => {
    setAiLookupRequest({ messageId: message.id, nonce: Date.now() });
  }, []);
  // A press belongs to the chat it was made in.
  useEffect(() => {
    setAiLookupRequest(null);
  }, [chatKey]);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportMessage, setReportMessage] = useState(null);
  const [reportExisting, setReportExisting] = useState(null);
  const [reportIsUpdate, setReportIsUpdate] = useState(false);

  const handleReportNotDelivered = useCallback(async (message) => {
    // Check for existing report before opening modal
    try {
      const response = await axios.get(`/api/delivery-reports/by-message/${message.id}`);
      setReportExisting(response.data);
      setReportIsUpdate(true);
    } catch {
      setReportExisting(null);
      setReportIsUpdate(false);
    }
    setReportMessage(message);
    setReportModalOpen(true);
  }, []);

  const handleReportModalClose = useCallback(() => {
    setReportModalOpen(false);
    setReportMessage(null);
    setReportExisting(null);
    setReportIsUpdate(false);
  }, []);

  const handleReportSuccess = useCallback((successMessage) => {
    dispatch(addNotification('deliveryReport', successMessage, NOTIFICATION_STATUS.SUCCESS));
  }, [dispatch]);

  // Determine contact carrier for the report message
  const reportContactCarrier = useMemo(() => {
    if (!reportMessage?.phone || !contact) return null;

    const normalizedPhone = (reportMessage.phone || '').replace(/\D/g, '').slice(-10);
    const phoneInfoList = [
      { phone: contact.phone1, info: contact.phone1Info },
      { phone: contact.phone2, info: contact.phone2Info },
      { phone: contact.phone3, info: contact.phone3Info },
    ];

    const match = phoneInfoList.find(({ phone }) => {
      const norm = (phone || '').replace(/\D/g, '').slice(-10);
      return norm && norm === normalizedPhone;
    });

    return match?.info?.carrier || null;
  }, [reportMessage, contact]);

  const getScrollToBottomVisibility = useCallback((container) => {
    if (!container) return;

    const { scrollHeight, scrollTop, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    setShowScrollToBottomButton(distanceFromBottom > 1000);
  }, []);

  const scrollMessagesToBottom = () => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  };

  const handleLoadMoreMessages = useCallback(() => {
    if (chat.nextCursor) {
      if (messagesRef.current) {
        scrollPositionRef.current = messagesRef.current?.scrollHeight;
      }
      dispatch(getMoreChatMessages(chat.nextCursor, chatKey));
    }
  }, [chat.nextCursor, chatKey]);

  const handleLoadMoreScheduledMessages = useCallback(() => {
    if (chat.scheduledNextCursor) {
      dispatch(getMoreScheduledChatMessages(chat.scheduledNextCursor, chatKey));
    }
  }, [chat.nextCursor, chatKey]);

  const handleShowHideScheduled = () => {
    setShowScheduledMessages((prev) => !prev);
  };

  useEffect(() => {
    if (chatKey) {
      dispatch(getCustomer(chatKey));
    }
    return () => {
      dispatch(clearFilteredChatMessages());
      dispatch(clearCustomerData());
      dispatch(setFileUploadToMessage(false));
    };
  }, [dispatch, chatKey]);

  useEffect(() => {
    // Validate chatKey is a valid positive integer to prevent fetching ALL messages
    // (server requires contactId, but this provides defense-in-depth)
    const chatKeyInt = parseInt(chatKey, 10);
    if (!chatKey || !chatKeyInt || chatKeyInt <= 0) return undefined;

    // Check if we've already attempted to load for this chatKey
    if (hasAttemptedLoadRef.current.has(chatKey)) {
      return undefined;
    }

    const chatMessages = chat?.messages || [];
    const chatScheduledMessages = chat?.scheduledMessages || [];
    const hasAnyMessages = chatMessages.length > 0 || chatScheduledMessages.length > 0;
    const needsHistoryFetch = !hasAnyMessages || chat?.isPartialFromSocket;

    // Load if no messages exist OR if chat was created from socket (partial)
    if (needsHistoryFetch && !isMessagesLoading) {
      console.log('[ChatDetails] 🔄 Loading messages for chatKey:', chatKey);

      // Mark this chatKey as attempted BEFORE dispatching (prevents race condition)
      hasAttemptedLoadRef.current.add(chatKey);

      // Dispatch both actions
      dispatch(getMoreChatMessages(null, chatKey, null));
      dispatch(getMoreScheduledChatMessages(null, chatKey, null));
    }

    // Cleanup when chatKey changes
    return () => {
      hasAttemptedLoadRef.current.delete(chatKey);
    };
  }, [chatKey, chat?.messages?.length, chat?.scheduledMessages?.length, dispatch]);

  useEffect(() => {
    if (chat.unreadCount) {
      const unreadMessageIds = messages
        .filter((message) => message.isReceived && message.readAt === null)
        .map((message) => message.id);

      if (!helpers.isEmpty(unreadMessageIds)) {
        dispatch(markChatAsSeen(chatKey, unreadMessageIds));
      }
    }
  }, [dispatch, chatKey, chat.unreadCount, messages]);

  useEffect(() => {
    if (messagesRef.current?.scrollHeight && scrollPositionRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight - scrollPositionRef.current;
    }
  }, [chat.nextCursor]);

  useEffect(() => {
    if (!helpers.isEmpty(messages)) {
      scrollMessagesToBottom();
    }
  }, [messages.at(-1), isMoreThanOnePhoneNumber, chat?.isPartialFromSocket]);

  if (!chatKey) {
    return <DefaultMessagesView />;
  }

  if (!!deletedChatKey && deletedChatKey === chatKey) {
    return <Redirect to={ROUTES.APP_MANAGEMENT_CHAT} />;
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Toolbar chat={chat} />
      <Divider />
      <Box
        component={PerfectScrollbar}
        options={{ suppressScrollX: true }}
        containerRef={(container) => {
          messagesRef.current = container;
        }}
        onScrollY={getScrollToBottomVisibility}
        sx={{
          padding: {
            xs: '24px 16px',
            md: '24px 36px',
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {canLoadMoreMessages && (
            <LoadMoreButton
              startIcon={<KeyboardArrowUpIcon />}
              disabled={isMessagesLoading || helpers.isEmpty(chat.nextCursor)}
              onClick={handleLoadMoreMessages}
              sx={{
                marginBottom: '24px',
              }}
            >
              Load previous messages
            </LoadMoreButton>
          )}
        </Box>
        {!helpers.isEmpty(messages) ? messages.map((message) => (
          <Message
            key={message.id}
            message={message}
            showPhoneNumber={isMoreThanOnePhoneNumber}
            userCanDeleteTexts={userCanDeleteTexts}
            canViewTestDeliverability={canViewTestDeliverability}
            onTestDeliverability={handleTestDeliverability}
            canReportNotDelivered={canReportNotDelivered}
            onReportNotDelivered={handleReportNotDelivered}
            lookItUpState={lookItUpState}
            onLookItUp={handleLookItUp}
          />
        )) : (
          <DefaultMessagesView />
        )}
        {showScheduledMessages && (
          <ScheduledMessagesList
            contactPhones={contactPhones}
            scheduledMessages={scheduledMessages}
            userRolePermissions={userRolePermissions}
            userCanDeleteTexts={userCanDeleteTexts}
            sendingLimits={sendingLimits}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {canLoadMoreScheduledMessages && messagesFilteredByPhone && (
                <LoadMoreButton
                  startIcon={<KeyboardArrowDownIcon />}
                  disabled={isMessagesLoading || helpers.isEmpty(chat.scheduledNextCursor)}
                  onClick={handleLoadMoreScheduledMessages}
                >
                  Show more scheduled messages
                </LoadMoreButton>
              )}
            </Box>
          </ScheduledMessagesList>
        )}
      </Box>
      <MessageAdd
        chat={chat}
        maxTextMessageLength={MESSAGES.MAX_CUSTOM_TEXT_MESSAGE_LENGTH}
        showScrollButton={showScrollToBottomButton}
        setShowScheduledMessages={setShowScheduledMessages}
        scrollMessagesToBottom={scrollMessagesToBottom}
        userRolePermissions={userRolePermissions}
        sendingLimits={sendingLimits}
        aiLookupRequest={aiLookupRequest}
        onLookItUpStateChange={setLookItUpState}
      >
        {!helpers.isEmpty(scheduledMessages) && (
          <Button
            size="small"
            variant="text"
            color="secondary"
            sx={{
              textTransform: 'none',
              padding: '0',
              ':hover': {
                background: 'none',
              },
            }}
            onClick={handleShowHideScheduled}
            disableRipple
          >
            {`Scheduled messages (${scheduledMessages.length})`}
            <ExpandMoreIcon
              sx={{
                transition: 'transform 0.3s ease',
                transform: showScheduledMessages ? 'rotate(-180deg)' : 'rotate(0deg)',
              }}
            />
          </Button>
        )}
      </MessageAdd>
      {canViewTestDeliverability && (
        <SmartComposeModal
          open={smartComposeOpen}
          onClose={handleSmartComposeClose}
          loading={scoreLoading}
          scoreData={scoreData}
          error={scoreError}
          sourceType="chatMessage"
          sourceId={smartComposeMessage?.id || null}
          initialTab="testDeliverability"
          messageBody={smartComposeMessage?.body || ''}
          messageBodyRaw={smartComposeMessage?.body || ''}
          attachmentBlobName={smartComposeMessage?.attachments?.[0] || null}
          attachmentContainer={smartComposeMessage?.messageAttachmentContainer || null}
          canViewTestDeliverability
          defaultCarrier={smartComposeCarrier.defaultCarrier}
          unsupportedCarrierName={smartComposeCarrier.unsupportedCarrierName}
        />
      )}
      {canReportNotDelivered && (
        <ReportNotDeliveredModal
          open={reportModalOpen}
          onClose={handleReportModalClose}
          message={reportMessage}
          contactName={contact ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim() : ''}
          contactCarrier={reportContactCarrier}
          isUpdate={reportIsUpdate}
          existingReport={reportExisting}
          onSuccess={handleReportSuccess}
        />
      )}
    </Box>
  );
}

export default ChatDetails;
